// Structured data for every rights-topic page on OSRR.
// 57 topics — exact slug list specified by the user.
// Rendered by /rights-topic/:slug via RightsTopicPage.jsx.

const logo = (domain) => `https://logo.clearbit.com/${domain}`;

// Small helper to keep resource objects terse and self-documenting.
const r = (name, url, domain, opts = {}) => ({ name, url, domain, ...opts });

export const RIGHTS_TOPICS = {
  "intellectual-property": {
    title: "Intellectual Property",
    overline: "IP foundations · WIPO · National copyright offices",
    intro:
      "Intellectual property is the umbrella body of law that turns a creative work into something a human being can own, license, sell, and inherit. For music, IP is the source from which every other right (mechanical, performance, sync, master) descends.",
    points: [
      "Music IP rests on two distinct copyrights — the composition (the song) and the master (the specific recording).",
      "International copyright enforcement is harmonised by treaties: Berne, Rome, WIPO Copyright Treaty, TRIPS.",
      "Most countries register IP at a national copyright office; international protection is coordinated through WIPO.",
    ],
    resources: [
      r("WIPO — World Intellectual Property Organization", "https://www.wipo.int", "wipo.int", { isGovernment: true }),
      r("U.S. Copyright Office", "https://www.copyright.gov", "copyright.gov", { isGovernment: true }),
      r("USPTO — U.S. Patent and Trademark Office", "https://www.uspto.gov", "uspto.gov", { isGovernment: true }),
      r("EUIPO — EU Intellectual Property Office", "https://www.euipo.europa.eu", "euipo.europa.eu", { isGovernment: true }),
    ],
  },

  "master-recording": {
    title: "Master Recording Rights & Royalties",
    overline: "Sound recording · Phonogram rights",
    intro:
      "A master recording is the specific captured performance of a song — distinct from the composition itself. Whoever owns the master controls how that recording is licensed, reproduced, streamed, and synced.",
    points: [
      "Master ownership traditionally sits with record labels; modern indie deals increasingly leave masters with the artist.",
      "Master royalties pay for streams, downloads, physical sales, sync placements, and neighbouring-rights uses.",
      "Master rights are separate from songwriter (composition) royalties — both flow on every commercial use.",
    ],
    resources: [
      r("SoundExchange", "https://www.soundexchange.com", "soundexchange.com"),
      r("RIAA — Recording Industry Association of America", "https://www.riaa.com", "riaa.com"),
      r("IFPI — International Federation of the Phonographic Industry", "https://www.ifpi.org", "ifpi.org"),
    ],
  },

  "master-rights": {
    title: "Master Rights & Royalties",
    overline: "Label · Artist · Phonogram producer",
    intro:
      "Master rights are the bundle of exclusive rights attaching to a sound recording: reproduction, distribution, public performance, and digital transmission. They are owned by the producer of the recording (label or self-released artist).",
    points: [
      "Master rights generate royalties from streams, downloads, sync, broadcasting, and neighbouring rights collections.",
      "An artist can split master rights from the song's composition rights — the two flow independently.",
      "Recapture of masters is governed by national copyright statutes (e.g. U.S. §203 termination after 35 years).",
    ],
    resources: [
      r("SoundExchange", "https://www.soundexchange.com", "soundexchange.com"),
      r("AARC — Alliance of Artists and Recording Companies", "https://www.aarcroyalties.com", "aarcroyalties.com"),
      r("RIAA", "https://www.riaa.com", "riaa.com"),
    ],
  },

  "mechanical-rights": {
    title: "Mechanical Rights & Royalties",
    overline: "Reproduction · §115 · MLC · MROs",
    intro:
      "Mechanical rights are the right to reproduce a composition in physical or digital form — CDs, vinyl, downloads, and on-demand streams. Mechanical Licensing Collectives (MLCs) and Mechanical Rights Organizations (MROs) issue licenses and collect royalties for songwriters and publishers.",
    points: [
      "In the U.S., statutory mechanicals are set under §115 of the Copyright Act and administered by The MLC.",
      "Internationally, MROs like MCPS (UK), GEMA Mechanical (DE), and JASRAC (JP) collect mechanicals for their territories.",
      "Streaming mechanicals are now the largest component of mechanical royalty income.",
    ],
    resources: [
      r("The MLC — Mechanical Licensing Collective (U.S.)", "https://www.themlc.com", "themlc.com", { isGovernment: true }),
      r("MCPS — Mechanical-Copyright Protection Society (UK)", "https://www.prsformusic.com", "prsformusic.com"),
      r("Harry Fox Agency", "https://www.harryfox.com", "harryfox.com"),
    ],
  },

  "performance-rights": {
    title: "Performance Rights & Royalties",
    overline: "PROs · Public performance · Broadcasting",
    intro:
      "Performance rights cover any public performance of a composition — live, radio, TV, restaurant, streaming. Performing Rights Organizations (PROs) license venues and broadcasters and distribute royalties to songwriters and publishers.",
    points: [
      "U.S. PROs: ASCAP, BMI, SESAC, GMR. International PROs include SACEM (FR), GEMA (DE), JASRAC (JP), PRS (UK), SOCAN (CA).",
      "Performance royalties are split 50/50 between the writer's share and the publisher's share by long-standing convention.",
      "Reciprocal agreements between PROs worldwide ensure a song collects royalties globally.",
    ],
    resources: [
      r("ASCAP", "https://www.ascap.com", "ascap.com"),
      r("BMI", "https://www.bmi.com", "bmi.com"),
      r("SESAC", "https://www.sesac.com", "sesac.com"),
      r("GMR — Global Music Rights", "https://globalmusicrights.com", "globalmusicrights.com"),
    ],
  },

  "music-publishing": {
    title: "Music Publishing Rights & Royalties",
    overline: "Catalog administration · Songwriter representation",
    intro:
      "Music publishers own or administer the copyrights in compositions on behalf of songwriters. They register works globally, license songs for every commercial use, and collect mechanical, performance, sync, and print royalties.",
    points: [
      "The 'majors' — Sony Music Publishing, Universal Music Publishing Group, Warner Chappell — administer roughly 60% of global compositions.",
      "Deal types: full publishing, co-publishing, sub-publishing (territorial), and admin-only.",
      "A publisher's primary job is registration, accounting, and pursuit of unclaimed royalties.",
    ],
    resources: [
      r("Sony Music Publishing", "https://www.sonymusicpub.com", "sonymusicpub.com"),
      r("Universal Music Publishing Group", "https://www.umusicpub.com", "umusicpub.com"),
      r("Warner Chappell Music", "https://www.warnerchappell.com", "warnerchappell.com"),
      r("Kobalt Music", "https://www.kobaltmusic.com", "kobaltmusic.com"),
      r("Songtrust", "https://www.songtrust.com", "songtrust.com"),
    ],
  },

  "synchronisation": {
    title: "Synchronisation Rights & Royalties",
    overline: "Sync · Film · TV · Advertising · Games",
    intro:
      "Synchronisation rights govern the use of a composition or master in timed relation to visual media — film, TV, video games, advertising, and online video. Each sync requires a license from BOTH the composition owner (publisher) and the master owner (label or artist).",
    points: [
      "Two licenses per sync: a sync license (composition) and a master use license (recording).",
      "Sync fees are negotiated case-by-case — no statutory rate, no PRO coverage of the up-front fee.",
      "Backend public-performance royalties on broadcast continue to flow through the PROs.",
    ],
    resources: [
      r("Sony Music Publishing — Sync", "https://www.sonymusicpub.com/en/services/sync-licensing", "sonymusicpub.com"),
      r("Songtradr", "https://www.songtradr.com", "songtradr.com"),
      r("Musicbed", "https://www.musicbed.com", "musicbed.com"),
    ],
  },

  "micro-synchronisation": {
    title: "Micro-Synchronisation Rights & Royalties",
    overline: "UGC · TikTok · Instagram · YouTube Shorts",
    intro:
      "Micro-sync is the licensing framework that lets short-form social platforms (TikTok, Reels, Shorts) embed music into user-generated content. Platforms strike blanket deals with publishers and labels; royalties are pooled and distributed per-stream.",
    points: [
      "Pioneered by TikTok's blanket UGC deals with the major publishers and labels.",
      "Royalty per use is small but volume is massive — micro-sync is now a real income stream for popular catalogs.",
      "Catalog availability on social platforms is governed by these blanket deals — when they expire, songs vanish.",
    ],
    resources: [
      r("TikTok for Music", "https://www.tiktok.com/business/en-US/inspirations/music", "tiktok.com"),
      r("Meta Music — Sound Collection", "https://business.facebook.com/business/help/musicguidelines", "facebook.com"),
      r("YouTube Audio Library", "https://studio.youtube.com/channel/UC/music", "youtube.com"),
    ],
  },

  "print-rights": {
    title: "Print Rights & Royalties",
    overline: "Sheet music · Lyric reprints · Folios",
    intro:
      "Print rights are the right to reproduce a composition in printed or visually-displayed form — sheet music, songbooks, lyric reprints, choral arrangements, and licensed digital scores.",
    points: [
      "Print royalties are licensed by the publisher; standard royalty is a percentage of retail per copy.",
      "Lyric display royalties (Genius, Musixmatch) are licensed separately from sheet music.",
      "Choral arrangements and educational use have their own customary print royalty rates.",
    ],
    resources: [
      r("Hal Leonard", "https://www.halleonard.com", "halleonard.com"),
      r("Alfred Music", "https://www.alfred.com", "alfred.com"),
      r("Musicnotes", "https://www.musicnotes.com", "musicnotes.com"),
      r("Sheet Music Plus", "https://www.sheetmusicplus.com", "sheetmusicplus.com"),
    ],
  },

  "tv-film-rights": {
    title: "TV & Film Rights & Royalties",
    overline: "Sync placement · Cue sheets · Broadcast",
    intro:
      "Every cue of music used in a film or TV programme generates sync and master-use revenue up-front, and ongoing public-performance royalties whenever the title is broadcast or streamed. Cue sheets filed by the producer drive PRO distributions.",
    points: [
      "Two flows: a one-time sync + master use fee at clearance, then ongoing performance royalties forever.",
      "Cue sheets list every musical work, its writer/publisher splits, and duration — submitted to PROs.",
      "Streaming-platform performance royalties (Netflix, Hulu, Prime) are negotiated as direct deals with PROs.",
    ],
    resources: [
      r("Motion Picture Association (MPA)", "https://www.motionpictures.org", "motionpictures.org"),
      r("AICP — Association of Independent Commercial Producers", "https://www.aicp.com", "aicp.com"),
      r("Crunch Digital", "https://www.crunchdigital.com", "crunchdigital.com"),
    ],
  },

  "digital-performance": {
    title: "Digital Performance Rights & Royalties",
    overline: "Non-interactive streaming · Webcasting",
    intro:
      "Digital performance rights cover non-interactive digital transmissions of sound recordings — internet radio, satellite radio, and certain cable services. In the U.S., these are collected by SoundExchange for performers and labels under §114 of the Copyright Act.",
    points: [
      "SoundExchange is the sole U.S. collective for §114 digital performance royalties (50% labels, 45% featured artist, 5% non-featured).",
      "Pandora, SiriusXM, and Apple Music Radio (non-interactive) are core licensees.",
      "Interactive streaming (Spotify, Apple Music on-demand) is handled by direct deals, not §114.",
    ],
    resources: [
      r("SoundExchange", "https://www.soundexchange.com", "soundexchange.com", { isGovernment: true }),
      r("AFM & SAG-AFTRA Intellectual Property Rights Distribution Fund", "https://www.afmsagaftrafund.org", "afmsagaftrafund.org"),
    ],
  },

  "subsequent-rights": {
    title: "Subsequent Rights & Royalties",
    overline: "Derivative works · Adaptations · Translations",
    intro:
      "Subsequent rights cover what happens to a composition or master after its first use — translations, adaptations, derivative recordings, sample clearances, and cover versions. Each subsequent use generates its own licensing chain.",
    points: [
      "A cover requires only a mechanical license (compulsory in many countries); an adaptation requires the publisher's permission.",
      "Translations are derivative works — only the publisher can authorise them.",
      "Sample clearance requires permission from both the original master owner and the publisher.",
    ],
    resources: [
      r("Easy Song Licensing", "https://www.easysonglicensing.com", "easysonglicensing.com"),
      r("Tracklib", "https://www.tracklib.com", "tracklib.com"),
    ],
  },

  "music-broadcasting": {
    title: "Music Broadcasting Rights & Royalties",
    overline: "Terrestrial radio · TV · Satellite",
    intro:
      "Music broadcasting rights govern the transmission of music over terrestrial, satellite, and cable channels. Broadcasters pay blanket licenses to PROs (for compositions) and, in most countries, to neighbouring-rights societies (for masters).",
    points: [
      "The U.S. is one of the few major markets where terrestrial AM/FM radio does NOT pay master royalties.",
      "Satellite and digital cable in the U.S. pay master royalties via SoundExchange.",
      "Outside the U.S., master broadcasting royalties are collected by neighbouring-rights societies (PPL UK, GVL DE, SCPP FR).",
    ],
    resources: [
      r("NAB — National Association of Broadcasters", "https://www.nab.org", "nab.org"),
      r("PPL (UK)", "https://www.ppluk.com", "ppluk.com"),
      r("GVL (Germany)", "https://www.gvl.de", "gvl.de"),
    ],
  },

  "music-streaming": {
    title: "Music Streaming Rights & Royalties",
    overline: "DSPs · On-demand · Pro-rata vs user-centric",
    intro:
      "Streaming royalties are paid by DSPs (Spotify, Apple Music, Amazon, YouTube Music, Tidal) for the right to stream music interactively. Each stream generates both a master royalty (to label/artist) and a mechanical + performance royalty (to publisher/songwriter).",
    points: [
      "Pro-rata model (the industry standard) pools all subscriber revenue and pays each rightsholder by share-of-streams.",
      "User-centric model (championed by Tidal, Deezer in some markets) pays each user's subscription pro-rata across only the artists they actually streamed.",
      "Streaming mechanicals in the U.S. are set under §115 by the Copyright Royalty Board (Phonorecords IV ruling).",
    ],
    resources: [
      r("Spotify for Artists", "https://artists.spotify.com", "spotify.com"),
      r("Apple Music for Artists", "https://artists.apple.com", "apple.com"),
      r("Distrokid", "https://distrokid.com", "distrokid.com"),
      r("U.S. Copyright Royalty Board", "https://www.crb.gov", "crb.gov", { isGovernment: true }),
    ],
  },

  "cross-collateralisation": {
    title: "Cross-Collateralisation",
    overline: "Contract clause · Recoupment",
    intro:
      "Cross-collateralisation is a contract clause that lets a label or publisher recoup unrecouped advances from one project against royalties earned on a different project. It is one of the most consequential terms in any music deal.",
    points: [
      "Without cross-collateralisation, each album recoups (or doesn't) on its own — favourable to the artist.",
      "With cross-collateralisation, label can hold royalties from a hit to pay for a flop — favourable to the company.",
      "Modern artist-friendly deals often exclude touring, merch, and sync income from the cross.",
    ],
    resources: [
      r("Recording Academy — Artist's Rights Resource", "https://www.recordingacademy.com", "recordingacademy.com"),
      r("Music Business Worldwide", "https://www.musicbusinessworldwide.com", "musicbusinessworldwide.com"),
    ],
  },

  "digital-music-copyrights": {
    title: "Digital Music Copyrights",
    overline: "Copyright in digital files · DMCA",
    intro:
      "Digital music copyrights extend traditional composition and master copyrights into digital files, streams, and downloads. They are the legal basis for take-downs, Content ID matching, and platform liability under the DMCA and EU Article 17.",
    points: [
      "DMCA §512 in the U.S. gives platforms a safe harbour if they expeditiously remove infringing content.",
      "EU Article 17 (formerly Article 13) shifts more liability onto platforms to license content proactively.",
      "Content ID (YouTube), Audible Magic, and Pex are the primary fingerprinting systems policing digital copyrights.",
    ],
    resources: [
      r("U.S. Copyright Office — DMCA", "https://www.copyright.gov/dmca/", "copyright.gov", { isGovernment: true }),
      r("YouTube Content ID", "https://support.google.com/youtube/answer/2797370", "youtube.com"),
      r("Audible Magic", "https://www.audiblemagic.com", "audiblemagic.com"),
    ],
  },

  "radio-play-rights": {
    title: "Radio Play Rights & Royalties",
    overline: "Terrestrial · Internet radio",
    intro:
      "Radio play rights cover the public performance of music over radio. Terrestrial AM/FM stations pay performance royalties to PROs for compositions; internet and satellite radio additionally pay master royalties to SoundExchange in the U.S.",
    points: [
      "Terrestrial U.S. radio is the world's largest blanket licensee of PRO repertoires but uniquely does not pay master royalties.",
      "Internet radio in the U.S. (Pandora, iHeartRadio) pays both composition (via PRO) and master (via SoundExchange).",
      "Foreign radio plays generate composition royalties that flow back to U.S. writers via reciprocal PRO agreements.",
    ],
    resources: [
      r("SoundExchange", "https://www.soundexchange.com", "soundexchange.com"),
      r("ASCAP Radio", "https://www.ascap.com/help/royalties-and-payment/radio", "ascap.com"),
      r("BMI Radio", "https://www.bmi.com/licensing/entry/radio", "bmi.com"),
    ],
  },

  "merchandise-rights": {
    title: "Merchandise Rights & Royalties",
    overline: "Apparel · Tour merch · Licensed goods",
    intro:
      "Merchandise rights cover the use of an artist's name, likeness, logo, lyrics, and trademarks on physical or digital goods. Royalties are paid by the merchandiser or licensee to the rights-holder, often split with the artist's label or manager.",
    points: [
      "Two layers: rights of publicity (name/likeness) and trademark rights (logos, slogans).",
      "Tour merch is the highest-margin revenue stream for most touring artists.",
      "Direct-to-fan platforms (Shopify, Bandcamp) increasingly bypass traditional merch licensees.",
    ],
    resources: [
      r("Bravado (Universal Music Group)", "https://www.bravado.com", "bravado.com"),
      r("Live Nation Merchandise", "https://www.livenationentertainment.com", "livenationentertainment.com"),
      r("Merch by Amazon", "https://merch.amazon.com", "amazon.com"),
    ],
  },

  "advertising-rights": {
    title: "Advertising Rights & Royalties",
    overline: "Commercials · Endorsements · Sponsorship",
    intro:
      "Advertising rights cover the use of music in commercials, branded content, and corporate films. Both the composition and the master require licenses, and rates are negotiated by duration, media, term, and territory.",
    points: [
      "Endorsement clauses sometimes prohibit licensing a song to a competing brand.",
      "Re-licensing for a renewed term is one of the most overlooked sources of follow-on revenue.",
      "AICP cue sheets feed PRO performance royalties on broadcast commercials.",
    ],
    resources: [
      r("AICP — Association of Independent Commercial Producers", "https://www.aicp.com", "aicp.com"),
      r("Marmoset Music", "https://www.marmosetmusic.com", "marmosetmusic.com"),
      r("MassiveMusic", "https://www.massivemusic.com", "massivemusic.com"),
    ],
  },

  "non-mechanical-rights": {
    title: "Non-Mechanical Rights & Royalties",
    overline: "Everything outside reproduction",
    intro:
      "'Non-mechanical' is an umbrella term for every revenue stream other than the reproduction of a song — performance, sync, print, merchandising, grand rights, and neighbouring rights. Understanding the difference is essential for accurate accounting.",
    points: [
      "Mechanical = reproducing the song (CD, download, stream).",
      "Non-mechanical = performing, displaying, or syncing the song in any other way.",
      "Most publishing deals treat the two categories differently for the purposes of advance recoupment.",
    ],
    resources: [
      r("MPA — Music Publishers Association", "https://www.mpa.org", "mpa.org"),
      r("NMPA — National Music Publishers' Association", "https://www.nmpa.org", "nmpa.org"),
    ],
  },

  "reproduction-rights": {
    title: "Re-Production Rights & Royalties",
    overline: "Re-records · Cover versions · Re-issues",
    intro:
      "Re-production rights govern re-recording an artist's own compositions, re-releasing a catalog, or otherwise creating a new master of a previously-recorded song. They are central to Taylor Swift-style master re-recordings.",
    points: [
      "Re-recording a song the artist wrote requires only a mechanical license (often statutory) — not the original label's permission.",
      "Re-recording a song someone else wrote requires only a §115-style mechanical license in the U.S.",
      "Standard label contracts include a re-record restriction blocking new versions for 5–10 years.",
    ],
    resources: [
      r("Harry Fox Agency", "https://www.harryfox.com", "harryfox.com"),
      r("The MLC", "https://www.themlc.com", "themlc.com", { isGovernment: true }),
    ],
  },

  "promotional-rights": {
    title: "Promotional Rights & Royalties",
    overline: "Promo use · Trailers · Snippets",
    intro:
      "Promotional rights cover the use of music in marketing materials — trailers, EPKs, lyric videos, social-media teasers, and platform-curated playlists. These uses are often royalty-free or low-rate, in exchange for promotional value.",
    points: [
      "Promo sync uses often carry no master royalty, only a token publishing royalty (or vice versa).",
      "Platform 'pitch placements' (Spotify Editorial, Apple Music for Artists) are unpaid but high-impact.",
      "An artist's contractual right to approve promo uses varies wildly by deal vintage.",
    ],
    resources: [
      r("Spotify for Artists", "https://artists.spotify.com", "spotify.com"),
      r("Apple Music for Artists", "https://artists.apple.com", "apple.com"),
    ],
  },

  "writers-rights": {
    title: "Writers' Rights & Royalties",
    overline: "Songwriter share · Reversion · §203 termination",
    intro:
      "Writers' rights are the bundle of moral, economic, and statutory rights belonging to the original creator of a composition. The most powerful U.S. example is §203 termination — the right to recapture a copyright 35 years after assignment.",
    points: [
      "Writers' share of performance royalties (50% of all PRO income) is paid DIRECTLY to the writer, not through the publisher.",
      "U.S. §203 lets writers terminate any post-1977 copyright assignment 35–40 years after the grant.",
      "Moral rights (paternity, integrity) are robust in EU/UK and weak in the U.S.",
    ],
    resources: [
      r("Songwriters Hall of Fame", "https://www.songhall.org", "songhall.org"),
      r("SGA — Songwriters Guild of America", "https://www.songwritersguild.com", "songwritersguild.com"),
      r("NSAI — Nashville Songwriters Association International", "https://www.nashvillesongwriters.com", "nashvillesongwriters.com"),
    ],
  },

  "illustrations-rights": {
    title: "Illustrations Rights & Royalties",
    overline: "Album art · Liner illustrations · Brand visuals",
    intro:
      "Illustrations rights govern visual artwork created for an album cover, music video, merch, or marketing campaign. Like music, the illustrator owns the copyright unless transferred — a work-for-hire clause is essential for the label/artist to own the artwork.",
    points: [
      "Without a written work-for-hire, the illustrator retains copyright in album art forever.",
      "Re-use of album art (T-shirts, posters, social) often requires a separate license from the original artist.",
      "AI-generated cover art carries unresolved questions of authorship and copyright eligibility.",
    ],
    resources: [
      r("Graphic Artists Guild", "https://graphicartistsguild.org", "graphicartistsguild.org"),
      r("AIGA — American Institute of Graphic Arts", "https://www.aiga.org", "aiga.org"),
    ],
  },

  "print-rights-visual": {
    title: "Print Rights & Royalties (Visual)",
    overline: "Posters · Books · Visual print reproductions",
    intro:
      "Print rights in the visual sense cover the reproduction of artwork, photography, and visual designs in printed media — posters, books, lyric folios, merchandise, and concert programs. Each reproduction requires a license from the visual rights-holder.",
    points: [
      "Reproduction royalties are typically a per-unit royalty or a flat license fee.",
      "Posters, books, and lyric folios may bundle multiple visual licenses (illustrator + photographer + designer).",
      "U.S. copyright registration before infringement unlocks statutory damages and attorney's fees.",
    ],
    resources: [
      r("U.S. Copyright Office — Visual Arts", "https://www.copyright.gov/registration/visual-arts", "copyright.gov", { isGovernment: true }),
      r("Artist Rights Society (ARS)", "https://arsny.com", "arsny.com"),
    ],
  },

  "recording-rights": {
    title: "Recording Rights & Royalties",
    overline: "Recording session · Performer royalties",
    intro:
      "Recording rights are the rights generated by participating in a recording session — featured artist royalties, non-featured (session musician) royalties, and producer royalties. They flow separately from songwriter and master-owner royalties.",
    points: [
      "Featured artists in the U.S. collect 45% of SoundExchange digital-performance royalties; session musicians get 5%.",
      "Producer royalties are 'producer points' — typically 3–5% of the artist royalty, sometimes paid as letter of direction.",
      "Internationally, neighbouring-rights societies distribute to performers — PPL UK, GVL DE, etc.",
    ],
    resources: [
      r("SoundExchange", "https://www.soundexchange.com", "soundexchange.com"),
      r("AFM — American Federation of Musicians", "https://www.afm.org", "afm.org"),
      r("SAG-AFTRA", "https://www.sagaftra.org", "sagaftra.org"),
    ],
  },

  "producers-rights": {
    title: "Producers' Rights & Royalties",
    overline: "Producer share · Points · Letters of direction",
    intro:
      "Producers' rights cover the producer's share of master royalties (producer points), backend public-performance royalties on production-related compositions, and any work-for-hire copyrights agreed in the production deal.",
    points: [
      "Producer points are paid out of the artist's master royalty, not on top of it — usually 3–5%.",
      "A 'letter of direction' instructs the label to pay the producer's share directly, ensuring royalty visibility.",
      "Producer composition contributions earn songwriter royalties via PRO splits.",
    ],
    resources: [
      r("Music Producers Guild", "https://www.mpg.org.uk", "mpg.org.uk"),
      r("Recording Academy — Producers & Engineers Wing", "https://www.recordingacademy.com/producers-and-engineers-wing", "recordingacademy.com"),
    ],
  },

  "video-rights": {
    title: "Video Rights & Royalties",
    overline: "Music videos · YouTube · Visualisers",
    intro:
      "Video rights cover audiovisual works featuring music — official music videos, visualisers, lyric videos, and concert films. The video itself is a separate copyrighted work distinct from the underlying composition or master.",
    points: [
      "Most music videos are work-for-hire commissioned by the label — label owns the visual copyright.",
      "YouTube generates revenue through ad share and the music industry's biggest royalty pool (via DSP + Content ID combined).",
      "Long-form concert films often involve grand rights / performance rights / sync rights stacked together.",
    ],
    resources: [
      r("VEVO", "https://www.vevo.com", "vevo.com"),
      r("YouTube Music", "https://music.youtube.com", "youtube.com"),
      r("DGA — Directors Guild of America", "https://www.dga.org", "dga.org"),
    ],
  },

  "domain-rights": {
    title: "Domain Rights",
    overline: "Domain names · Cybersquatting · UDRP",
    intro:
      "Domain rights cover ownership and disputes over internet domain names. Artists and labels routinely register variations of their brand to prevent cybersquatting and use UDRP arbitration to recover infringing domains.",
    points: [
      "ICANN coordinates the global DNS system and accredits domain registrars.",
      "UDRP (Uniform Domain-Name Dispute-Resolution Policy) lets a trademark holder recover an abusive domain.",
      "Best practice: register name.com, name.net, name.org plus any common misspellings.",
    ],
    resources: [
      r("ICANN", "https://www.icann.org", "icann.org", { isGovernment: true }),
      r("WIPO Arbitration & Mediation — UDRP", "https://www.wipo.int/amc/en/domains/", "wipo.int", { isGovernment: true }),
    ],
  },

  "website-rights": {
    title: "Website Rights",
    overline: "Site copyrights · Terms of use · Embeds",
    intro:
      "Website rights cover the copyrighted content, design, and code of a website. For music sites, terms of use must address fan-uploaded content, embedded music, and merchandise IP. Properly drafted terms protect against DMCA and platform-liability claims.",
    points: [
      "A website is a copyrightable compilation; its source code is independently copyrightable.",
      "Embedded music or video must be properly licensed — embed code alone does not confer rights.",
      "GDPR/CCPA require disclosure of any cookies and tracking even on small music sites.",
    ],
    resources: [
      r("U.S. Copyright Office", "https://www.copyright.gov", "copyright.gov", { isGovernment: true }),
      r("Creative Commons", "https://creativecommons.org", "creativecommons.org"),
    ],
  },

  "commercial-rights": {
    title: "Commercial Rights & Royalties",
    overline: "Commercial use · For-profit licensing",
    intro:
      "Commercial rights are the right to use a composition or master in any for-profit context — advertising, retail in-store music, corporate events, films, games, and ringtones. Commercial use generally requires a paid license; rates depend on duration, media, term, and territory.",
    points: [
      "Public-performance licenses for commercial venues (restaurants, shops, gyms) are blanket-licensed by PROs.",
      "Background-music providers (Mood Media, Soundtrack Your Brand) sub-license the PRO rights.",
      "Corporate event use, ringtones, and in-store playlists each have their own licensing pipelines.",
    ],
    resources: [
      r("Mood Media", "https://us.moodmedia.com", "moodmedia.com"),
      r("Soundtrack Your Brand", "https://www.soundtrackyourbrand.com", "soundtrackyourbrand.com"),
      r("BMI Business Music", "https://www.bmi.com/licensing", "bmi.com"),
    ],
  },

  "interactive-technology": {
    title: "Interactive Technology Rights",
    overline: "AR · VR · Games · Apps",
    intro:
      "Interactive technology rights cover the use of music in interactive experiences — video games, AR/VR, fitness apps, in-app music, and metaverse environments. Licensing is bespoke and often blends sync, master use, and software embedding rights.",
    points: [
      "Game music licenses combine sync + master use + sometimes work-for-hire composing.",
      "VR/AR experiences raise novel spatialised-audio and embodied-performance questions still being litigated.",
      "Peloton, Zwift, Apple Fitness+ have all signed bespoke deals with publishers and labels to cover their interactive use.",
    ],
    resources: [
      r("Game Music Industry Association", "https://www.gmiaonline.org", "gmiaonline.org"),
      r("ASCAP — Games & Audio Branding", "https://www.ascap.com", "ascap.com"),
    ],
  },

  "branding-anthropology": {
    title: "Branding (Anthropology)",
    overline: "Cultural identity · Heritage · Story-led brands",
    intro:
      "Anthropological branding is the practice of building a music brand on the cultural, geographic, and historical identity of its origin — region, dialect, dress, ritual. Done well it differentiates an artist or label from algorithmically-generated competitors.",
    points: [
      "Anthropological branding turns local identity into global commercial advantage.",
      "Visual identity, language choice, dress, and symbolism all carry trademarkable weight.",
      "Cultural-appropriation risk requires explicit consent or partnership with the originating community.",
    ],
    resources: [
      r("Smithsonian Folkways", "https://folkways.si.edu", "si.edu", { isGovernment: true }),
      r("UNESCO Intangible Cultural Heritage", "https://ich.unesco.org", "unesco.org", { isGovernment: true }),
    ],
  },

  "branding-international": {
    title: "Branding (International)",
    overline: "Cross-border identity · Trademark protection",
    intro:
      "International branding governs how an artist or label establishes and protects identity across multiple territories. It requires coordinated trademark filing, domain control, and adaptation of brand assets to each market.",
    points: [
      "Madrid Protocol lets a single trademark application protect a brand in 100+ countries.",
      "Localisation (language, colour, symbolism) is critical for genuine market entry.",
      "International touring + merch requires trademark coverage in every territory played.",
    ],
    resources: [
      r("WIPO Madrid System (international trademarks)", "https://www.wipo.int/madrid", "wipo.int", { isGovernment: true }),
      r("Interbrand", "https://www.interbrand.com", "interbrand.com"),
    ],
  },

  "branding-national": {
    title: "Branding (National)",
    overline: "Domestic identity · National trademarks",
    intro:
      "National branding covers the artist's or label's commercial identity within a single country — trademarks, trade dress, and domain ownership filed at the national copyright/trademark office. The U.S. uses the USPTO; each country has its own equivalent.",
    points: [
      "U.S. trademarks are filed with the USPTO; first-to-use rights matter even before registration.",
      "Trademark classes 9 (recordings), 25 (apparel), and 41 (entertainment services) cover most music brands.",
      "State right-of-publicity laws (California Civil Code §3344, NY Civil Rights Law §50/51) add another layer.",
    ],
    resources: [
      r("USPTO — U.S. Patent and Trademark Office", "https://www.uspto.gov", "uspto.gov", { isGovernment: true }),
      r("UK Intellectual Property Office", "https://www.gov.uk/government/organisations/intellectual-property-office", "gov.uk", { isGovernment: true }),
    ],
  },

  "dj-rights": {
    title: "DJ Rights",
    overline: "Mixes · Sets · Club & festival licensing",
    intro:
      "DJ rights cover the rights involved in performing recorded music in clubs, festivals, mixes, livestreams, and radio shows. A DJ set typically requires multiple layers of licensing: venue public-performance, master use for any recorded set, and clearance for any sampled or remixed content.",
    points: [
      "Live club performance is covered by the venue's PRO blanket license.",
      "Pre-recorded DJ mixes uploaded to platforms require label/master permission via services like Dubset / Pex.",
      "Festival livestreams add sync-style rights on top of normal performance rights.",
    ],
    resources: [
      r("Mixcloud", "https://www.mixcloud.com", "mixcloud.com"),
      r("Pex", "https://pex.com", "pex.com"),
      r("Dubset (acquired by Pex)", "https://pex.com", "pex.com"),
    ],
  },

  "non-commercial-rights": {
    title: "Non-Commercial Rights & Royalties",
    overline: "Educational · Charitable · Public-interest use",
    intro:
      "Non-commercial rights cover uses of music in educational, charitable, religious, and other non-profit contexts. Many jurisdictions provide statutory exceptions or reduced rates, but the licenses are not free by default.",
    points: [
      "U.S. educational use exemptions are narrow — face-to-face teaching is covered, but not most digital distribution.",
      "Religious-service performance is exempt in many jurisdictions but recording the service is not.",
      "Creative Commons offers public-license templates for creators who want non-commercial reuse permitted.",
    ],
    resources: [
      r("Creative Commons", "https://creativecommons.org", "creativecommons.org"),
      r("CCLI (Christian Copyright Licensing International)", "https://www.ccli.com", "ccli.com"),
    ],
  },

  "distribution-rights": {
    title: "Distribution Rights & Royalties",
    overline: "Physical & digital distribution",
    intro:
      "Distribution rights govern how music is delivered to consumers — physical (CD, vinyl) and digital (DSPs, downloads, social platforms). Distributors take a percentage of the gross or charge a flat fee and pass royalties through to labels and independent artists.",
    points: [
      "Major-label distribution is integrated (UMG via Virgin Music, Sony via The Orchard, WMG via ADA).",
      "Indie aggregators (DistroKid, TuneCore, CD Baby, Amuse) deliver directly to all major DSPs.",
      "Physical distribution still matters for collector formats — vinyl revenue has grown 16 years in a row.",
    ],
    resources: [
      r("The Orchard (Sony)", "https://www.theorchard.com", "theorchard.com"),
      r("Virgin Music Group", "https://www.virginmusic.com", "virginmusic.com"),
      r("DistroKid", "https://distrokid.com", "distrokid.com"),
      r("TuneCore", "https://www.tunecore.com", "tunecore.com"),
    ],
  },

  "resale-royalties": {
    title: "Re-Sale Royalties",
    overline: "Droit de suite · Secondary-market royalties",
    intro:
      "Re-sale royalties (droit de suite) entitle the original creator to a percentage of every subsequent sale of their work. They are well-established for visual art in the EU/UK, contested in the U.S., and increasingly debated for vinyl, memorabilia, and NFTs.",
    points: [
      "EU Artist's Resale Right pays 0.25%–4% of secondary art sale prices to the original creator.",
      "California Resale Royalties Act was largely preempted by U.S. federal copyright law in 2018.",
      "NFTs experiment with smart-contract enforced resale royalties — uneven enforcement across marketplaces.",
    ],
    resources: [
      r("DACS — Design and Artists Copyright Society (UK)", "https://www.dacs.org.uk", "dacs.org.uk"),
      r("ADAGP — Société des Auteurs Dans les Arts Graphiques et Plastiques (FR)", "https://www.adagp.fr", "adagp.fr"),
    ],
  },

  "copyright-rights": {
    title: "Copyright Rights & Royalties",
    overline: "Foundational copyright · Authorship",
    intro:
      "Copyright is the foundational right that protects original creative works — including musical compositions and sound recordings — and underpins every royalty stream in the industry. Copyrights arise automatically upon fixation but registration unlocks critical enforcement remedies.",
    points: [
      "U.S. registration before infringement unlocks statutory damages ($750–$150,000 per work) and attorney's fees.",
      "Copyright term is typically life of the author + 70 years (life+50 in some jurisdictions).",
      "Joint authorship makes each co-author an equal owner with independent licensing rights unless contracted otherwise.",
    ],
    resources: [
      r("U.S. Copyright Office", "https://www.copyright.gov", "copyright.gov", { isGovernment: true }),
      r("WIPO Copyright Treaty", "https://www.wipo.int/treaties/en/ip/wct/", "wipo.int", { isGovernment: true }),
      r("Berne Convention", "https://www.wipo.int/treaties/en/ip/berne/", "wipo.int", { isGovernment: true }),
    ],
  },

  "domain-rights-registration": {
    title: "Domain Rights (Registration)",
    overline: "Registrars · TLDs · WHOIS",
    intro:
      "Registering a domain name is the first step in establishing an artist or label's online identity. Registrars sell registrations on behalf of registries; ICANN accredits both.",
    points: [
      "Always own your own domain rather than letting a developer or label hold it on your behalf.",
      "Lock domain transfers and enable two-factor authentication on the registrar account.",
      "Renew well in advance — lapsed domains can be auction-grabbed within hours.",
    ],
    resources: [
      r("ICANN", "https://www.icann.org", "icann.org", { isGovernment: true }),
      r("Namecheap", "https://www.namecheap.com", "namecheap.com"),
      r("Cloudflare Registrar", "https://www.cloudflare.com/products/registrar/", "cloudflare.com"),
      r("Google Domains (now Squarespace)", "https://www.squarespace.com/domains", "squarespace.com"),
    ],
  },

  "club-rights": {
    title: "Club Rights",
    overline: "Nightclub · Bar · Venue licensing",
    intro:
      "Club rights cover the public-performance licensing needed to play recorded or live music in nightclubs, bars, and similar venues. Most clubs hold blanket PRO licenses; in many countries an additional master-rights blanket is required.",
    points: [
      "In the U.S., a club typically holds ASCAP + BMI + SESAC + GMR blanket licenses (the 'PRO stack').",
      "Outside the U.S., venue licenses bundle composition AND master royalties (e.g. PPL+PRS in the UK).",
      "Background-music providers (Soundtrack Your Brand, Mood) can package the licensing for the venue.",
    ],
    resources: [
      r("ASCAP — Restaurants, Bars & Nightclubs", "https://www.ascap.com/help/Licensing/Restaurants-Bars-Nightclubs", "ascap.com"),
      r("BMI — Restaurants, Bars & Music Venues", "https://www.bmi.com/licensing/entry/restaurants_bars_nightclubs", "bmi.com"),
      r("PPL PRS (UK)", "https://pplprs.co.uk", "pplprs.co.uk"),
    ],
  },

  "trademark-logo-rights": {
    title: "Trademark and Logo Rights & Royalties",
    overline: "Marks · Logos · Brand protection",
    intro:
      "Trademarks protect names, logos, slogans, and any distinctive sign that identifies the source of goods or services. For music brands, trademarks unlock merchandise revenue and block competitors from confusingly similar names.",
    points: [
      "Trademark rights in the U.S. accrue from use, but federal registration with the USPTO unlocks nationwide priority.",
      "Logos can be both copyrighted (as artwork) and trademarked (as a brand identifier).",
      "Madrid Protocol enables international filing across 100+ countries from a single application.",
    ],
    resources: [
      r("USPTO — Trademarks", "https://www.uspto.gov/trademarks", "uspto.gov", { isGovernment: true }),
      r("WIPO Madrid System", "https://www.wipo.int/madrid", "wipo.int", { isGovernment: true }),
      r("EUIPO — EU Trademarks", "https://www.euipo.europa.eu", "euipo.europa.eu", { isGovernment: true }),
    ],
  },

  "hosting": {
    title: "Hosting",
    overline: "Web hosting · DMCA notice & takedown",
    intro:
      "Hosting providers run the servers that store and deliver website content and music files. They are subject to DMCA safe-harbour rules in the U.S. and equivalent laws abroad — meaning rightsholders can compel takedown of infringing files.",
    points: [
      "A hosting provider must register a DMCA agent with the U.S. Copyright Office to qualify for safe harbour.",
      "Major music platforms (Spotify, Apple Music) host audio in proprietary CDNs separate from origin storage.",
      "Cloud hosting providers (AWS, Google Cloud, Azure) provide both the storage and the CDN layer.",
    ],
    resources: [
      r("AWS — Amazon Web Services", "https://aws.amazon.com", "aws.amazon.com"),
      r("Google Cloud", "https://cloud.google.com", "cloud.google.com"),
      r("Cloudflare", "https://www.cloudflare.com", "cloudflare.com"),
      r("U.S. Copyright Office — DMCA Agent Directory", "https://www.copyright.gov/dmca-directory/", "copyright.gov", { isGovernment: true }),
    ],
  },

  "whois": {
    title: "Whois",
    overline: "Domain ownership lookup · GDPR redaction",
    intro:
      "Whois is the public directory of domain-name registrations — historically including the registrant's name, email, address, and phone. GDPR forced wide redaction of personal Whois data in 2018; legitimate-interest lookups still possible via registrar.",
    points: [
      "Pre-GDPR Whois was a key tool for trademark holders investigating cybersquatting; today it shows mostly registrar-proxy data.",
      "ICANN's RDAP (Registration Data Access Protocol) is the modern replacement for Whois.",
      "UDRP and URS proceedings can compel a registrar to disclose redacted Whois data.",
    ],
    resources: [
      r("ICANN Lookup (RDAP/Whois)", "https://lookup.icann.org", "icann.org", { isGovernment: true }),
      r("Whois.com", "https://www.whois.com", "whois.com"),
    ],
  },

  "licensing": {
    title: "Licensing",
    overline: "All licensing pipelines · Cross-reference hub",
    intro:
      "Licensing is the umbrella process by which any musical right is granted — mechanical, performance, sync, print, master, sample, or sub-publishing. The right license at the right rate from the right party is the spine of every commercial use of music.",
    points: [
      "Mechanical: from the publisher or MLC (statutory in many countries).",
      "Public performance: from a PRO (ASCAP, BMI, SESAC, GMR, or the relevant national PRO).",
      "Sync: from both the publisher AND the master owner — two licenses always.",
      "Sample: from both the original publisher AND the original label, plus a sample-clearance fee.",
    ],
    resources: [
      r("Harry Fox Agency", "https://www.harryfox.com", "harryfox.com"),
      r("The MLC", "https://www.themlc.com", "themlc.com", { isGovernment: true }),
      r("Easy Song Licensing", "https://www.easysonglicensing.com", "easysonglicensing.com"),
      r("Songtradr", "https://www.songtradr.com", "songtradr.com"),
    ],
  },

  "icann": {
    title: "ICANN",
    overline: "Internet Corporation for Assigned Names and Numbers",
    intro:
      "ICANN is the non-profit that coordinates the global DNS system — the root, the gTLDs (.com, .net, .org, .music), and the country-code TLDs. Every domain in the world is allocated under ICANN policy.",
    points: [
      "ICANN accredits the registrars that sell domain names to the public.",
      "ICANN's UDRP process resolves trademark-vs-domain disputes globally.",
      "ICANN expanded the gTLD program beyond .com / .net / .org to include hundreds of new domain extensions.",
    ],
    resources: [
      r("ICANN", "https://www.icann.org", "icann.org", { isGovernment: true }),
      r("ICANN Lookup (RDAP/Whois)", "https://lookup.icann.org", "icann.org", { isGovernment: true }),
      r("WIPO Arbitration — UDRP", "https://www.wipo.int/amc/en/domains/", "wipo.int", { isGovernment: true }),
    ],
  },

  // ============ Genres / Practice Areas ============

  "jazz": {
    title: "Jazz",
    overline: "Jazz rights · Improvisation · Standards",
    intro:
      "Jazz raises distinct rights questions because the music is largely improvised and built on a deep catalogue of standards. Tin Pan Alley copyrights, public-domain works, and improvised performances each have their own clearance pathway.",
    points: [
      "The 'Great American Songbook' contains many standards still under copyright; pre-1928 U.S. works are largely public domain.",
      "Improvisations on a standard generate the standard's PRO royalty plus an arrangement credit when fixed.",
      "Jazz reissues frequently raise master-rights orphan-work questions.",
    ],
    resources: [
      r("Jazz Foundation of America", "https://jazzfoundation.org", "jazzfoundation.org"),
      r("Smithsonian Jazz", "https://americanhistory.si.edu/smithsonian-jazz", "si.edu", { isGovernment: true }),
      r("ASCAP Jazz", "https://www.ascap.com", "ascap.com"),
    ],
  },

  "delta-blues": {
    title: "Delta Blues",
    overline: "Mississippi blues · Field recordings · Public-domain",
    intro:
      "Delta blues is the foundational acoustic blues genre of the Mississippi Delta. Early field recordings raise complex rights issues — many original artists were paid token fees and never compensated for the catalogue's enduring commercial life.",
    points: [
      "Many pre-1923 blues recordings entered the U.S. public domain in 2024 under the CLASSICS Act timeline.",
      "Field-recording rights are often held by the recording engineer's estate or institution (Library of Congress).",
      "Descendants of original blues artists have ongoing legal campaigns to recover historic royalties.",
    ],
    resources: [
      r("Library of Congress — American Folklife Center", "https://www.loc.gov/folklife/", "loc.gov", { isGovernment: true }),
      r("Mississippi Blues Trail", "https://msbluestrail.org", "msbluestrail.org"),
      r("Blues Foundation", "https://blues.org", "blues.org"),
    ],
  },

  "classical": {
    title: "Classical",
    overline: "Public domain · Grand rights · Editions",
    intro:
      "Classical music sits at the intersection of public-domain compositions and copyrighted performances. The composition (Beethoven, Bach, Mozart) is free; the recording is not. Modern editions and arrangements can re-acquire copyright protection.",
    points: [
      "Pre-1928 classical compositions are largely U.S. public domain; the recording is not.",
      "Grand rights — opera, ballet, musical theatre — require dramatic-rights licenses separate from PRO blanket coverage.",
      "Critical editions and new arrangements of public-domain works are newly copyrighted.",
    ],
    resources: [
      r("Boosey & Hawkes", "https://www.boosey.com", "boosey.com"),
      r("G. Schirmer", "https://www.gschirmer.com", "gschirmer.com"),
      r("League of American Orchestras", "https://americanorchestras.org", "americanorchestras.org"),
    ],
  },

  "gospel": {
    title: "Gospel",
    overline: "NEW · Sacred music rights · Religious-service exemption",
    intro:
      "Gospel music has unique rights pathways combining standard music licensing with religious-service exemptions and church-targeted blanket licensing services like CCLI. Many traditional gospel compositions are public domain; modern gospel is fully copyrighted.",
    points: [
      "Religious-service performance is exempt in many jurisdictions — recording the service is not.",
      "CCLI (Christian Copyright Licensing International) provides church-targeted blanket licensing.",
      "Traditional spirituals are largely public-domain; arrangements and new lyrics carry fresh copyright.",
    ],
    resources: [
      r("CCLI — Christian Copyright Licensing International", "https://www.ccli.com", "ccli.com"),
      r("Gospel Music Association", "https://gospelmusic.org", "gospelmusic.org"),
      r("Stellar Awards", "https://thestellarawards.com", "thestellarawards.com"),
    ],
  },

  "indian": {
    title: "Indian",
    overline: "Indian classical · Bollywood · IPRS",
    intro:
      "Indian music rights are coordinated through IPRS (Indian Performing Right Society) for compositions and PPL India for recordings. Bollywood film music carries layered rights — producer, music director, lyricist, and playback singer.",
    points: [
      "IPRS is India's principal PRO; PPL India administers neighbouring rights.",
      "Bollywood film soundtracks historically vest in the producer under §17(b) of India's Copyright Act.",
      "Indian classical music's raga-based composition system raises distinct authorship attribution questions.",
    ],
    resources: [
      r("IPRS — Indian Performing Right Society", "https://www.iprs.org", "iprs.org"),
      r("PPL India", "https://pplindia.org", "pplindia.org"),
      r("Indian Music Industry (IMI)", "https://www.indianmi.org", "indianmi.org"),
    ],
  },

  "eastern-music": {
    title: "Eastern Music",
    overline: "Asian musical traditions · Cross-border licensing",
    intro:
      "Eastern music encompasses the rights frameworks of East and Southeast Asia — JASRAC (Japan), KOMCA (Korea), MCSC (China), MÜST (Taiwan), COMPASS (Singapore). Reciprocal agreements with U.S./EU PROs route Western royalties back home.",
    points: [
      "JASRAC is the world's third-largest PRO by collection size after ASCAP and BMI.",
      "China's MCSC has rapidly expanded since the 2010s; piracy enforcement remains uneven.",
      "K-pop's global rise has dramatically increased Korean writer royalty flows through KOMCA.",
    ],
    resources: [
      r("JASRAC (Japan)", "https://www.jasrac.or.jp", "jasrac.or.jp"),
      r("KOMCA (Korea)", "https://www.komca.or.kr", "komca.or.kr"),
      r("MCSC (China)", "https://www.mcsc.com.cn", "mcsc.com.cn"),
      r("COMPASS (Singapore)", "https://www.compass.org.sg", "compass.org.sg"),
    ],
  },

  "marching-bands": {
    title: "Marching Bands",
    overline: "Field shows · Arrangements · Drum corps",
    intro:
      "Marching band performances raise specific licensing questions because they typically use arrangements of popular copyrighted songs. Drum Corps International and most school bands work with specialised arrangement licensors.",
    points: [
      "School marching band arrangements of pop hits require both arrangement permission and ongoing performance licensing.",
      "Drum Corps International (DCI) holds blanket licenses for its competing corps.",
      "BluePrint Music Licensing and J.W. Pepper specialise in marching-band arrangements.",
    ],
    resources: [
      r("DCI — Drum Corps International", "https://www.dci.org", "dci.org"),
      r("J.W. Pepper", "https://www.jwpepper.com", "jwpepper.com"),
      r("BluePrint Music Licensing", "https://www.blueprintmusiclicensing.com", "blueprintmusiclicensing.com"),
    ],
  },

  "theatre-rights": {
    title: "Theatre Rights",
    overline: "Grand rights · Musical theatre · Off-Broadway",
    intro:
      "Theatre rights — also called 'grand rights' or 'dramatic performance rights' — cover the staged performance of musical theatre works. They are licensed directly by the rights-holder (composer/lyricist/book writer/estate), not through PROs.",
    points: [
      "Grand rights are NEVER part of a PRO blanket — they're always direct.",
      "Major theatre licensors: Music Theatre International (MTI), Concord Theatricals, Tams-Witmark.",
      "Off-Broadway, regional, and educational performances each have distinct rate tiers.",
    ],
    resources: [
      r("MTI — Music Theatre International", "https://www.mtishows.com", "mtishows.com"),
      r("Concord Theatricals", "https://www.concordtheatricals.com", "concordtheatricals.com"),
      r("Dramatists Guild of America", "https://www.dramatistsguild.com", "dramatistsguild.com"),
    ],
  },

  "orchestras": {
    title: "Orchestra's",
    overline: "Symphony rights · Mechanical · Hire libraries",
    intro:
      "Orchestral performance rights cover the live and recorded performance of symphonic works. Many orchestral works are public-domain compositions in copyrighted modern editions; rentals from publisher hire libraries supply the performance parts.",
    points: [
      "Performance materials are typically rented from a hire library (Boosey & Hawkes, Schott, Universal Edition).",
      "Modern editions of public-domain works carry fresh copyright in the edition.",
      "Orchestral recordings raise both master rights (label/orchestra) and underlying composition rights.",
    ],
    resources: [
      r("League of American Orchestras", "https://americanorchestras.org", "americanorchestras.org"),
      r("Boosey & Hawkes Hire", "https://www.boosey.com/pages/cr/hire/", "boosey.com"),
      r("Schott Music", "https://en.schott-music.com", "schott-music.com"),
      r("Universal Edition", "https://www.universaledition.com", "universaledition.com"),
    ],
  },

  "us-copyright-royalty-board": {
    title: "U.S. Copyright Royalty Board",
    overline: "Statutory rates · §115 · §114",
    intro:
      "The U.S. Copyright Royalty Board (CRB) is the panel of three federal judges that sets compulsory statutory royalty rates — most notably §115 (mechanicals) and §114 (digital sound recording performance). Its proceedings — 'Phonorecords' (mechanical) and 'Web' (webcasting) — set the rates DSPs, MLCs, and SoundExchange use.",
    points: [
      "Phonorecords IV (2023) set streaming mechanical rates through 2027.",
      "Web V (2021) set digital sound-recording royalty rates for non-interactive streaming through 2025.",
      "CRB rulings are appealable to the D.C. Circuit Court of Appeals.",
    ],
    resources: [
      r("U.S. Copyright Royalty Board", "https://www.crb.gov", "crb.gov", { isGovernment: true }),
      r("U.S. Copyright Office", "https://www.copyright.gov", "copyright.gov", { isGovernment: true }),
      r("The MLC", "https://www.themlc.com", "themlc.com", { isGovernment: true }),
      r("SoundExchange", "https://www.soundexchange.com", "soundexchange.com"),
    ],
  },
};

export const TOPIC_SLUGS = Object.keys(RIGHTS_TOPICS);

export const logoUrl = logo;
